// Updated: 14 Jan 2026 18:23:37
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DollarSign, CreditCard, Download, AlertCircle, Star, Crown, Check, Loader, Award, BadgePlus, Flame, Package, Send, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import PayPalModal from '../payments/PayPalModal';
import VendorPackages from './VendorPackages';
import './PointsManagement.css';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_SUB_CLIENT_ID || process.env.REACT_APP_PAYPAL_CLIENT_ID;

const PointsManagement = ({ user, onUserUpdate, t, language }) => {
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [showPayPalModal, setShowPayPalModal] = useState(false);
  const [showWithdrawOptions, setShowWithdrawOptions] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [withdrawForm, setWithdrawForm] = useState({
    points: '',
    paypalEmail: ''
  });
  const [loading, setLoading] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const paypalButtonsRef = useRef({});

  useEffect(() => {
    loadWithdrawalHistory();
    loadSubscriptionData();
    loadPayPalScript();
  }, []);

  const loadPayPalScript = () => {
    // Remove any existing PayPal SDK that doesn't support subscriptions
    const existingScripts = document.querySelectorAll('script[src*="paypal.com/sdk"]');
    const hasSubscriptionSDK = Array.from(existingScripts).some(s =>
      s.src.includes('intent=subscription') && s.src.includes(PAYPAL_CLIENT_ID)
    );

    if (hasSubscriptionSDK && window.paypal) {
      setPaypalLoaded(true);
      return;
    }

    // Remove old PayPal SDK if it doesn't support subscriptions
    if (window.paypal) {
      delete window.paypal;
      existingScripts.forEach(s => {
        if (!s.src.includes('intent=subscription')) {
          s.remove();
        }
      });
    }

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
    script.id = 'paypal-subscription-sdk';
    script.async = true;
    script.onload = () => {
      console.log('PayPal Subscription SDK loaded successfully');
      setPaypalLoaded(true);
    };
    script.onerror = (err) => {
      console.error('Failed to load PayPal SDK:', err);
      toast.error('Failed to load payment system');
    };
    document.body.appendChild(script);
  };

  useEffect(() => {
    if (paypalLoaded && selectedPlan && !activeSubscription) {
      renderPayPalButton(selectedPlan);
    }
  }, [paypalLoaded, selectedPlan, activeSubscription]);

  const loadWithdrawalHistory = async () => {
    try {
      const response = await axios.get('/api/payments/withdrawals/history');
      setWithdrawalHistory(response.data);
    } catch (error) {
      console.error('Load withdrawal history error:', error);
    }
  };

  const loadSubscriptionData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get('/api/subscriptions/plans'),
        axios.get('/api/subscriptions/my-subscription')
      ]);
      setSubscriptionPlans(plansRes.data);
      setActiveSubscription(subRes.data.subscription);
    } catch (error) {
      console.error('Load subscription data error:', error);
    }
  };

  const renderPayPalButton = (plan) => {
    const containerId = `paypal-button-container-${plan.tier}`;
    const container = document.getElementById(containerId);

    console.log('Rendering PayPal button for plan:', plan.tier, 'planId:', plan.planId);

    if (!container) {
      console.error('Container not found:', containerId);
      return;
    }

    if (!window.paypal) {
      console.error('PayPal SDK not loaded');
      return;
    }

    if (!plan.planId) {
      console.error('Plan ID is missing for tier:', plan.tier);
      toast.error('Payment configuration error. Please contact support.');
      return;
    }

    // Clear previous button
    container.innerHTML = '';

    try {
      window.paypal.Buttons({
        style: {
          shape: 'rect',
          color: 'gold',
          layout: 'vertical',
          label: 'subscribe'
        },
        createSubscription: function(data, actions) {
          console.log('Creating subscription with plan_id:', plan.planId);
          return actions.subscription.create({
            plan_id: plan.planId
          });
        },
        onApprove: async function(data) {
          console.log('Subscription approved:', data.subscriptionID);
          setProcessingPayment(true);
          try {
            const response = await axios.post('/api/subscriptions/create', {
              subscriptionId: data.subscriptionID,
              tier: plan.tier
            });

            toast.success(`Subscribed! ${response.data.pointsAwarded.toLocaleString()} points added!`);
            onUserUpdate();
            loadSubscriptionData();
            setSelectedPlan(null);
          } catch (error) {
            console.error('Subscription error:', error);
            toast.error(error.response?.data?.message || 'Failed to activate subscription');
          } finally {
            setProcessingPayment(false);
          }
        },
        onCancel: function() {
          console.log('Payment cancelled by user');
          toast.error('Payment cancelled');
        },
        onError: function(err) {
          console.error('PayPal error:', err);
          toast.error('Payment error. Please try again.');
        }
      }).render(`#${containerId}`).catch(err => {
        console.error('PayPal render error:', err);
        toast.error('Failed to load payment buttons');
      });
    } catch (err) {
      console.error('PayPal button creation error:', err);
      toast.error('Payment system error');
    }
  };

  const handleWithdrawal = async (e) => {
    e.preventDefault();

    if (!withdrawForm.points || parseInt(withdrawForm.points) < 1000) {
      toast.error(t?.('minimumWithdrawalError') || 'Minimum withdrawal is 1000 points');
      return;
    }

    if (parseInt(withdrawForm.points) > 59500) {
      toast.error(t?.('maximumWithdrawalError') || 'Maximum withdrawal is 59500 points');
      return;
    }

    if (user.points < parseInt(withdrawForm.points)) {
      toast.error(t?.('insufficientPoints') || 'Insufficient points');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/payments/withdraw', {
        points: parseInt(withdrawForm.points),
        paypalEmail: withdrawForm.paypalEmail || user.email
      });

      toast.success(t?.('withdrawalRequestSuccess') || 'Withdrawal request submitted successfully!');
      setShowWithdrawForm(false);
      setWithdrawForm({ points: '', paypalEmail: '' });
      onUserUpdate();
      loadWithdrawalHistory();
    } catch (error) {
      const message = error.response?.data?.message || (t?.('withdrawalRequestFailed') || 'Withdrawal request failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalPurchase = async (purchaseData) => {
    try {
      await onUserUpdate();
      toast.success(`Successfully purchased ${purchaseData.points} points!`);
    } catch (error) {
      console.error('Purchase update error:', error);
    }
  };

  const calculateWithdrawalAmount = (points) => {
    return ((points / 100) * 0.95).toFixed(2);
  };

  const closeWithdrawForm = () => {
    setShowWithdrawForm(false);
    setWithdrawForm({ points: '', paypalEmail: '' });
  };

  const getTierStyle = (tier) => {
    const styles = {
      basic: {
        gradient: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
        border: '#6b7280',
        accent: '#9ca3af',
        badge: 'STARTER'
      },
      standard: {
        gradient: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)',
        border: '#3b82f6',
        accent: '#60a5fa',
        badge: 'POPULAR'
      },
      plus: {
        gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
        border: '#8b5cf6',
        accent: '#a78bfa',
        badge: 'VALUE'
      },
      premium: {
        gradient: 'linear-gradient(135deg, #78350f 0%, #d97706 100%)',
        border: '#f59e0b',
        accent: '#fbbf24',
        badge: 'PREMIUM'
      },
      max: {
        gradient: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #fbbf24 100%)',
        border: '#fbbf24',
        accent: '#fef3c7',
        badge: 'BEST VALUE'
      }
    };
    return styles[tier] || styles.basic;
  };

  const getTierIcon = (tier) => {
    const icons = {
      basic: <Award className="w-5 h-5" />,
      standard: <Star className="w-5 h-5" />,
      plus: <BadgePlus className="w-5 h-5" />,
      premium: <Flame className="w-5 h-5" />,
      max: <Crown className="w-5 h-5" />
    };
    return icons[tier] || icons.basic;
  };

  return (
    <div className="space-y-6">
      {/* Weekly Deals Section */}
      <div style={{
        background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: '12px',
        padding: '24px',
        border: '2px solid #4a00e0'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h3 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            background: 'linear-gradient(90deg, #f59e0b, #fbbf24, #f59e0b)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>
            {t?.('weeklyPointDeals') || 'Weekly Point Deals'}
          </h3>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>
            {t?.('subscriptionDescription') || 'Get 50% points instantly + 50% dripping daily for 7 days'}
          </p>
        </div>

        {activeSubscription && (
          <div style={{
            background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
            border: '2px solid #10b981',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Check className="w-5 h-5" style={{ color: '#34d399' }} />
              <span style={{ fontWeight: 'bold', color: '#34d399', fontSize: '18px' }}>
                {t?.('activePlan') || 'Active'}: {t?.('tier' + activeSubscription.tier.charAt(0).toUpperCase() + activeSubscription.tier.slice(1)) || activeSubscription.tier.charAt(0).toUpperCase() + activeSubscription.tier.slice(1)}
              </span>
            </div>
            <p style={{ color: '#a7f3d0', fontSize: '14px', marginBottom: '8px' }}>
              {t?.('dayProgress') || 'Day'} {activeSubscription.daysCompleted}/7 • {activeSubscription.pointsAwarded?.toLocaleString()}/{activeSubscription.totalPoints?.toLocaleString()} {t?.('pointsReceived') || 'points received'}
            </p>
            <div style={{
              width: '100%',
              background: '#064e3b',
              borderRadius: '9999px',
              height: '8px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(activeSubscription.daysCompleted / 7) * 100}%`,
                background: 'linear-gradient(90deg, #34d399, #10b981)',
                height: '100%',
                borderRadius: '9999px',
                transition: 'width 0.3s'
              }}></div>
            </div>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          {subscriptionPlans.map(plan => {
            const style = getTierStyle(plan.tier);
            const isSelected = selectedPlan?.tier === plan.tier;

            return (
              <div
                key={plan.tier}
                style={{
                  background: style.gradient,
                  borderRadius: '12px',
                  border: `2px solid ${isSelected ? '#fbbf24' : style.border}`,
                  padding: '20px',
                  position: 'relative',
                  overflow: 'hidden',
                  transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                  transition: 'all 0.2s',
                  boxShadow: isSelected ? '0 0 20px rgba(251, 191, 36, 0.3)' : 'none'
                }}
              >
                {/* Badge */}
                {(plan.tier === 'max' || plan.tier === 'standard') && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '-30px',
                    background: plan.tier === 'max' ? 'linear-gradient(90deg, #dc2626, #f59e0b)' : '#3b82f6',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    padding: '4px 30px',
                    transform: 'rotate(45deg)'
                  }}>
                    {style.badge}
                  </div>
                )}

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ color: style.accent }}>{getTierIcon(plan.tier)}</span>
                  <h4 style={{ color: 'white', fontWeight: 'bold', fontSize: '20px', margin: 0 }}>
                    {t?.('tier' + plan.tier.charAt(0).toUpperCase() + plan.tier.slice(1)) || plan.name}
                  </h4>
                </div>

                {/* Price */}
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <span style={{ color: style.accent, fontSize: '14px' }}>$</span>
                  <span style={{ color: 'white', fontSize: '36px', fontWeight: 'bold' }}>
                    {plan.price.toFixed(2).split('.')[0]}
                  </span>
                  <span style={{ color: style.accent, fontSize: '18px' }}>
                    .{plan.price.toFixed(2).split('.')[1]}
                  </span>
                  <span style={{ color: '#9ca3af', fontSize: '12px', display: 'block' }}>{t?.('perWeek') || '/week'}</span>
                </div>

                {/* Points Info */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '8px',
                    padding: '12px',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: '#fbbf24', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                      {plan.totalPoints.toLocaleString()}
                    </p>
                    <p style={{ color: '#9ca3af', fontSize: '12px', margin: 0 }}>{t?.('totalPoints') || 'TOTAL POINTS'}</p>
                  </div>
                </div>

                <div style={{ fontSize: '13px', color: '#d1d5db', marginBottom: '16px' }}>
                  <p style={{ margin: '4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#34d399' }}>✓</span>
                    {plan.instantPoints.toLocaleString()} {t?.('instant') || 'instant'}
                  </p>
                  <p style={{ margin: '4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#34d399' }}>✓</span>
                    {plan.dailyPoints.toLocaleString()}{t?.('perDay') || '/day'} × 7 {t?.('days') || 'days'}
                  </p>
                  <p style={{ margin: '4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#fbbf24' }}>★</span>
                    {plan.pointsPerDollar} {t?.('ptsPerDollar') || 'pts per $1'}
                  </p>
                </div>

                {/* Action */}
                {activeSubscription ? (
                  <button
                    disabled
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      background: '#374151',
                      color: '#6b7280',
                      border: 'none',
                      cursor: 'not-allowed',
                      fontSize: '14px'
                    }}
                  >
                    {t?.('alreadyActive') || 'Already Active'}
                  </button>
                ) : isSelected ? (
                  <div>
                    <div
                      id={`paypal-button-container-${plan.tier}`}
                      style={{ minHeight: '45px' }}
                    >
                      {!paypalLoaded && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                          <Loader className="w-5 h-5 animate-spin" style={{ color: style.accent }} />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedPlan(null)}
                      style={{
                        width: '100%',
                        marginTop: '8px',
                        padding: '8px',
                        borderRadius: '6px',
                        background: 'transparent',
                        color: '#9ca3af',
                        border: '1px solid #4b5563',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {t?.('cancel') || 'Cancel'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setSelectedPlan(plan)}
                    disabled={processingPayment}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                      color: '#000',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    {t?.('selectPlan') || 'Select Plan'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>



      {/* Points Overview */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-orange-500">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-white">
          <DollarSign className="w-5 h-5 text-yellow-400" />
          {t?.('pointsManagement') || 'Points Management'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-yellow-900 p-4 rounded-lg border border-yellow-600 text-center">
            <p className="text-3xl font-bold text-yellow-400">{user.points?.toLocaleString()}</p>
            <p className="text-yellow-200">{t?.('currentPoints') || 'Current Points'}</p>
          </div>
          <div className="bg-green-900 p-4 rounded-lg border border-green-600 text-center">
            <p className="text-3xl font-bold text-green-400">${((user.points / 100) * 0.95).toFixed(2)}</p>
            <p className="text-green-200">{t?.('estimatedValue') || 'Estimated Value'}</p>
          </div>
        </div>

        <div className="flex gap-2">
          {user.role === 'main_admin' && (
            <button
              onClick={() => setShowPayPalModal(true)}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <CreditCard className="w-4 h-4" />
              {t?.('buyPoints') || 'Buy Points'}
            </button>
          )}
          <button
            onClick={() => setShowWithdrawOptions(true)}
            disabled={user.points < 100}
            className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {t?.('withdraw') || 'Withdraw'}
          </button>
        </div>

        {user.points < 100 && (
          <div className="mt-3 p-3 bg-yellow-900 border border-yellow-600 rounded">
            <p className="text-yellow-200 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {t?.('minimumWithdrawal') || 'Minimum withdrawal amount is 1000 points ($9.50)'}
            </p>
          </div>
        )}
      </div>

      {/* Withdrawal History */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-orange-500">
        <h3 className="text-xl font-bold mb-4 text-white">{t?.('withdrawalHistory') || 'Withdrawal History'}</h3>
        <div className="space-y-3">
          {withdrawalHistory.length === 0 ? (
            <p className="text-gray-400">{t?.('noWithdrawalRequests') || 'No withdrawal requests yet.'}</p>
          ) : (
            withdrawalHistory.map(withdrawal => (
              <div key={withdrawal._id} className={`p-4 rounded-lg border-2 ${
                withdrawal.status === 'approved' ? 'border-green-600 bg-green-900' :
                withdrawal.status === 'rejected' ? 'border-red-600 bg-red-900' :
                'border-yellow-600 bg-yellow-900'
              }`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-white">
                      {withdrawal.points} {t?.('points') || 'Points'} → ${withdrawal.amount.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-300">PayPal: {withdrawal.paypalEmail}</p>
                    <p className="text-xs text-gray-400">{new Date(withdrawal.createdAt).toLocaleString()}</p>
                  </div>
                  <span className={`px-3 py-1 rounded text-sm font-bold ${
                    withdrawal.status === 'approved' ? 'bg-green-600 text-white' :
                    withdrawal.status === 'rejected' ? 'bg-red-600 text-white' :
                    'bg-yellow-600 text-white'
                  }`}>
                    {withdrawal.status.toUpperCase()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* PayPal Modal for regular purchases */}
      <PayPalModal
        isOpen={showPayPalModal}
        onClose={() => setShowPayPalModal(false)}
        onPurchase={handlePayPalPurchase}
        t={t}
      />


      {/* Withdraw Options Modal */}
      {showWithdrawOptions && !selectedVendor && !showWithdrawForm && createPortal(
        <div
          className="points-withdrawal-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowWithdrawOptions(false)}
        >
          <div className="points-withdrawal-modal" style={{ maxWidth: '600px' }}>
            <button onClick={() => setShowWithdrawOptions(false)} className="points-withdrawal-modal-close">×</button>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '8px',
              background: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center'
            }}>
              {(language === 'he' ? 'בחר שיטת משיכה' : (t?.('chooseWithdrawMethod') || 'Choose Withdrawal Method'))}
            </h2>
            <p style={{ color: '#9ca3af', textAlign: 'center', marginBottom: '24px', fontSize: '14px' }}>
              {(language === 'he' ? 'בחר כיצד למשוך את הנקודות שלך' : language === 'ar' ? 'اختر كيف تريد سحب نقاطك' : 'Select how you want to withdraw your points')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* PayPal Option */}
              <button
                onClick={() => { setShowWithdrawOptions(false); setShowWithdrawForm(true); }}
                disabled={user.points < 1000}
                style={{
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #0d2137 100%)',
                  border: '2px solid #3b82f6',
                  borderRadius: '12px',
                  padding: '20px',
                  cursor: user.points >= 1000 ? 'pointer' : 'not-allowed',
                  opacity: user.points >= 1000 ? 1 : 0.5,
                  transition: 'all 0.3s',
                  textAlign: 'left'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CreditCard size={24} style={{ color: '#fff' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#fff', fontWeight: '600', fontSize: '18px', marginBottom: '4px' }}>
                      {(language === 'he' ? 'משיכה דרך PayPal' : language === 'ar' ? 'السحب عبر PayPal' : 'Withdraw via PayPal')}
                    </div>
                    
                  </div>
                  <div style={{ color: '#3b82f6', fontSize: '14px', fontWeight: '500' }}>
                    $0.095/pt
                  </div>
                </div>
              </button>

                            {/* Ryder Option */}
              <div style={{
                background: 'linear-gradient(135deg, #1a2e1a 0%, #132613 100%)',
                border: '2px solid #22c55e',
                borderRadius: '12px',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <button
                  onClick={() => setSelectedVendor('ryder')}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Package size={24} style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: '600', fontSize: '18px', marginBottom: '4px' }}>
                      Ryder
                    </div>
                    
                  </div>
                </button>
                <a
                  href="https://t.me/ryderont"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    background: 'linear-gradient(135deg, #0088cc, #006699)',
                    borderRadius: '20px',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: '500',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Send size={14} />
                  Telegram
                </a>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Vendor Packages Modal */}
      {selectedVendor && createPortal(
        <div
          className="points-withdrawal-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setSelectedVendor(null)}
        >
          <div className="points-withdrawal-modal" style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <button
              onClick={() => setSelectedVendor(null)}
              className="points-withdrawal-modal-close"
            >×</button>
            <VendorPackages user={user} onUserUpdate={onUserUpdate} t={t} filterVendor={selectedVendor} onClose={() => setSelectedVendor(null)} language={language} />
          </div>
        </div>,
        document.body
      )}

      {/* Withdrawal Form Modal */}
      {showWithdrawForm && createPortal(
        <div
          className="points-withdrawal-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && closeWithdrawForm()}
        >
          <div className="points-withdrawal-modal" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeWithdrawForm} className="points-withdrawal-modal-close" disabled={loading}>×</button>
            <h3>
              <Download className="w-5 h-5 text-green-400" />
              {t?.('requestWithdrawal') || 'Request Withdrawal'}
            </h3>

            <form onSubmit={handleWithdrawal} className="points-withdrawal-form">
              <div className="points-withdrawal-field">
                <label className="points-withdrawal-label">{t?.('pointsToWithdraw') || 'Points to Withdraw'}</label>
                <input
                  type="number"
                  value={withdrawForm.points}
                  onChange={(e) => setWithdrawForm({...withdrawForm, points: e.target.value})}
                  className="points-withdrawal-input"
                  min="1000"
                  max={Math.min(user.points, 59500)}
                  placeholder={`Min: 1000, Max: ${Math.min(user.points, 59500)}`}
                  disabled={loading}
                />
                {withdrawForm.points && (
                  <p className="points-withdrawal-estimate">
                    {t?.('youllReceive') || "You'll receive"}: ${calculateWithdrawalAmount(withdrawForm.points)} ({t?.('feePercent') || '5% fee'})
                  </p>
                )}
              </div>

              <div className="points-withdrawal-field">
                <label className="points-withdrawal-label">{t?.('paypalEmail') || 'PayPal Email'}</label>
                <input
                  type="email"
                  value={withdrawForm.paypalEmail}
                  onChange={(e) => setWithdrawForm({...withdrawForm, paypalEmail: e.target.value})}
                  className="points-withdrawal-input"
                  placeholder={user.email}
                  disabled={loading}
                />
                <p className="points-withdrawal-email-hint">{t?.('leaveEmptyToUse') || 'Leave empty to use'}: {user.email}</p>
              </div>

              <div className="points-withdrawal-info">
                <p>
                  <strong>{t?.('info') || 'Info'}:</strong><br/>
                  • {t?.('withdrawalMinimum') || 'Minimum: 1000 points ($9.50)'}<br/>
                  • {t?.('withdrawalFee') || 'Platform fee: 5%'}<br/>
                  • {t?.('withdrawalProcessing') || 'Processing: 24-96 hours'}
                </p>
              </div>

              <div className="points-withdrawal-actions">
                <button
                  type="submit"
                  disabled={loading || !withdrawForm.points || parseInt(withdrawForm.points) < 1000}
                  className="points-withdrawal-btn points-withdrawal-btn-submit"
                >
                  {loading ? (t?.('submitting') || 'Submitting...') : (t?.('submitRequest') || 'Submit Request')}
                </button>
                <button
                  type="button"
                  onClick={closeWithdrawForm}
                  disabled={loading}
                  className="points-withdrawal-btn points-withdrawal-btn-cancel"
                >
                  {t?.('cancel') || 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {processingPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <Loader className="w-10 h-10 animate-spin" style={{ margin: '0 auto 16px' }} />
            <p style={{ fontSize: '18px' }}>{t?.('processingSubscription') || 'Processing your subscription...'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointsManagement;
